import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * App Mode determines whether the application uses demo data or real production data.
 *
 * - **demo**: Local-only IndexedDB with sample data. Safe for exploration and training.
 *   Data can be reset at any time without consequences.
 *
 * - **real**: Production database with optional Supabase sync. Contains actual user data.
 *   Data is precious and should never be automatically cleared.
 *
 * Each mode uses a completely separate IndexedDB database to prevent any data cross-contamination.
 */

export type AppMode = 'demo' | 'real';

interface AppModeState {
  /** Current application mode */
  mode: AppMode | null;

  /** Whether the user has completed initial mode selection */
  initialSetupComplete: boolean;

  /** Switch to demo mode (triggers page reload) */
  switchToDemo: () => void;

  /** Switch to real mode (triggers page reload) */
  switchToReal: () => void;

  /** Complete initial setup with selected mode */
  completeSetup: (mode: AppMode) => void;

  /** Reset demo data flag (allows re-seeding) */
  resetDemoDataFlag: () => void;
}

/** localStorage key for demo mode seeded flag */
const DEMO_SEEDED_KEY = 'demo-mode-seeded';

export const useAppMode = create<AppModeState>()(
  persist(
    (set, get) => ({
      mode: null,
      initialSetupComplete: false,

      switchToDemo: () => {
        const currentMode = get().mode;
        if (currentMode === 'demo') return;

        set({ mode: 'demo' });
        // Reload to reinitialize database with demo mode
        window.location.reload();
      },

      switchToReal: () => {
        const currentMode = get().mode;
        if (currentMode === 'real') return;

        set({ mode: 'real' });
        // Reload to reinitialize database with real mode
        window.location.reload();
      },

      completeSetup: (mode: AppMode) => {
        set({
          mode,
          initialSetupComplete: true,
        });
        // Reload to initialize the correct database
        window.location.reload();
      },

      resetDemoDataFlag: () => {
        localStorage.removeItem(DEMO_SEEDED_KEY);
      },
    }),
    {
      name: 'app-mode-storage',
      partialize: (state) => ({
        mode: state.mode,
        initialSetupComplete: state.initialSetupComplete,
      }),
    }
  )
);

/**
 * Get current app mode synchronously from localStorage.
 * Used during database initialization before React/Zustand hydration.
 * Returns 'demo' as default if no mode is set (safe default for new users).
 */
export function getAppModeSync(): AppMode {
  try {
    const stored = localStorage.getItem('app-mode-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.state?.mode) {
        return parsed.state.mode as AppMode;
      }
    }
  } catch {
    // Ignore parse errors
  }
  // Default to demo for safety (no accidental production data operations)
  return 'demo';
}

/**
 * Check if initial setup has been completed synchronously.
 * Used to determine if mode picker should be shown.
 */
export function isInitialSetupComplete(): boolean {
  try {
    const stored = localStorage.getItem('app-mode-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state?.initialSetupComplete === true;
    }
  } catch {
    // Ignore parse errors
  }
  return false;
}

/**
 * Get database name based on current app mode.
 * Demo and Real modes use completely separate IndexedDB databases.
 */
export function getDatabaseName(): string {
  const mode = getAppModeSync();
  return mode === 'demo' ? 'industrial-audit-demo' : 'industrial-audit-real';
}

/**
 * Check if demo data has been seeded.
 */
export function isDemoDataSeeded(): boolean {
  return localStorage.getItem(DEMO_SEEDED_KEY) === 'true';
}

/**
 * Mark demo data as seeded.
 */
export function markDemoDataSeeded(): void {
  localStorage.setItem(DEMO_SEEDED_KEY, 'true');
}

/**
 * Clear demo data seeded flag (allows re-seeding).
 */
export function clearDemoDataSeededFlag(): void {
  localStorage.removeItem(DEMO_SEEDED_KEY);
}
